import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink, MapPin, Phone, Star, X } from 'lucide-react';
import dentalFacialBanner from '../assets/sponsors/jaj-dental-facial-naoko-dds.png';
import floridaFudousanImage from '../assets/sponsors/florida-fudousan.png';
import hiroJapaneseRestaurantImage from '../assets/sponsors/hiro-japanese-restaurant.png';
import jacksonvilleJapaneseSchoolImage from '../assets/sponsors/jacksonville-japanese-school.png';
import saturiwaTradingCoImage from '../assets/sponsors/saturiwa-trading-co.png';
import solaceToshiImage from '../assets/sponsors/solace-toshi.png';

const SponsorsSection: React.FC = () => {
  const { t } = useTranslation();
  const [selectedSponsorImage, setSelectedSponsorImage] = useState<{
    alt: string;
    src: string;
  } | null>(null);

  useEffect(() => {
    if (!selectedSponsorImage) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedSponsorImage(null);
      }
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedSponsorImage]);

  const sponsors = [
    {
      name: "Jacksonville Japanese School",
      description: "ジャクソンビル日本語学校",
      website: "jacksonvillejapaneseschool.org",
      url: "https://www.jacksonvillejapaneseschool.org/",
      color: "from-blue-500 to-blue-700",
      image: jacksonvilleJapaneseSchoolImage
    },
    {
      name: "Florida Fudousan",
      description: "フロリダ不動産",
      website: "jacksonvillefudousan.com",
      url: "https://jacksonvillefudousan.com/",
      color: "from-green-600 to-teal-700",
      image: floridaFudousanImage
    },
    {
      name: "NAO Permanent Makeup",
      description: "Beauty Services",
      website: "naomakeup.com",
      url: "https://naomakeup.com",
      color: "from-purple-600 to-pink-600"
    },
    {
      name: "Hiro Japanese Restaurant",
      description: "Japanese Cuisine",
      website: "Hirojapanesefl.com",
      url: "https://Hirojapanesefl.com",
      color: "from-red-600 to-red-800",
      image: hiroJapaneseRestaurantImage
    },
    {
      name: "ひびなび",
      description: "Community Platform",
      website: "Jacksonville.vivinavi.com",
      url: "https://Jacksonville.vivinavi.com",
      color: "from-orange-500 to-orange-700"
    },
    {
      name: "Saturiwa Trading Co.",
      description: "Premium Spices, Meats, Caviar & Ingredients For Culinary Excellence!",
      website: "saturiwatrading.com",
      url: "https://saturiwatrading.com",
      color: "from-amber-600 to-orange-600",
      image: saturiwaTradingCoImage
    },
    {
      name: "Solace Toshi",
      description: "Hair Styling",
      phone: "904-625-7427",
      color: "from-stone-700 to-zinc-900",
      image: solaceToshiImage
    },
    {
      name: "Dental Facial Rejuvenation",
      description: "Dr. Naoko \"Nikki\" Fukushima, DDS",
      website: "dentalfacialrej.com",
      url: "https://www.dentalfacialrej.com",
      phone: "407-720-5812",
      address: "316 N. Alafaya Trail, Orlando, FL 32828",
      color: "from-pink-500 to-rose-600",
      image: dentalFacialBanner
    }
  ];

  return (
    <div>
      <div className="mb-7">
        <p className="eyebrow">Community partners</p>
        <h2 className="section-title">{t('sponsors.title')}</h2>
        <p className="mt-3 text-sm leading-6 text-[#667085]">
          We are grateful for the support of our community partners who help make our events and programs possible.
        </p>
      </div>

      {/* Sponsors Grid */}
      <div className="mb-8 grid grid-cols-1 gap-5">
          {sponsors.map((sponsor) => (
            <div
              key={sponsor.name}
              className="surface overflow-hidden transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(23,32,51,0.10)]"
            >
              {sponsor.image ? (
                <>
                  <div className={`bg-gradient-to-r ${sponsor.color} p-5 text-white`}>
                    <div className="mb-3 flex items-center justify-between">
                      <Star className="h-5 w-5" />
                      {sponsor.url && <ExternalLink className="w-5 h-5 opacity-70" />}
                    </div>
                    <h3 className="mb-1 text-lg font-extrabold">{sponsor.name}</h3>
                    <p className="text-sm opacity-90">{sponsor.description}</p>
                  </div>
                  {sponsor.address ? (
                    <div className="p-5">
                      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-start">
                        <div className="space-y-2 text-sm leading-relaxed text-gray-600">
                          {sponsor.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-pink-600" />
                              <a href={`tel:${sponsor.phone.replace(/-/g, '')}`} className="hover:text-pink-700">
                                {sponsor.phone}
                              </a>
                            </p>
                          )}
                          <p className="flex items-start gap-2">
                            <MapPin className="mt-0.5 h-4 w-4 flex-none text-pink-600" />
                            <span>{sponsor.address}</span>
                          </p>
                          {sponsor.website && <p>{sponsor.website}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedSponsorImage({
                              alt: `${sponsor.name} sponsor banner`,
                              src: sponsor.image,
                            })
                          }
                          className="group mx-auto w-full max-w-[220px] rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 sm:max-w-[180px] sm:justify-self-end"
                          aria-label={`Enlarge ${sponsor.name} sponsor banner`}
                        >
                          <img
                            src={sponsor.image}
                            alt={`${sponsor.name} sponsor banner`}
                            className="w-full rounded-lg object-contain opacity-80 transition duration-200 group-hover:opacity-100 group-hover:shadow-md"
                          />
                        </button>
                      </div>
                      {sponsor.url && (
                        <a
                          href={sponsor.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary mt-4 w-full"
                        >
                          Visit Website
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 sm:p-5">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-2 text-sm leading-relaxed text-gray-600">
                          {sponsor.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-pink-600" />
                              <a href={`tel:${sponsor.phone.replace(/-/g, '')}`} className="hover:text-pink-700">
                                {sponsor.phone}
                              </a>
                            </p>
                          )}
                          {sponsor.website && <p>{sponsor.website}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedSponsorImage({
                              alt: `${sponsor.name} sponsor banner`,
                              src: sponsor.image,
                            })
                          }
                          className="group mx-auto w-full max-w-[260px] rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 sm:mx-0 sm:w-auto sm:max-w-[180px] sm:flex-none"
                          aria-label={`Enlarge ${sponsor.name} sponsor banner`}
                        >
                          <img
                            src={sponsor.image}
                            alt={`${sponsor.name} sponsor banner`}
                            className="max-h-40 w-full rounded-lg object-contain opacity-80 transition duration-200 group-hover:opacity-100 group-hover:shadow-md"
                          />
                        </button>
                      </div>
                      {sponsor.url && (
                        <a
                          href={sponsor.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary mt-4 w-full"
                        >
                          Visit Website
                        </a>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className={`bg-gradient-to-r ${sponsor.color} p-5 text-white`}>
                    <div className="mb-3 flex items-center justify-between">
                      <Star className="h-5 w-5" />
                      <ExternalLink className="w-5 h-5 opacity-70" />
                    </div>
                    <h3 className="mb-1 text-lg font-extrabold">{sponsor.name}</h3>
                    <p className="text-sm opacity-90">{sponsor.description}</p>
                  </div>
                  <div className="p-5">
                    <div className="mb-4 space-y-2 text-sm text-gray-600">
                      <p>{sponsor.website}</p>
                    </div>
                    <a 
                      href={sponsor.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary w-full"
                    >
                      Visit Website
                    </a>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

      {/* Call to Action for New Sponsors */}
      <div className="border-l-4 border-[#d95f75] bg-[#172033] p-6 text-white">
        <h3 className="text-xl font-extrabold">
          {t('sponsors.getNoticed')}
        </h3>
        <p className="mb-5 mt-2 text-sm text-white/70">
          Partner with JAJ to reach the Japanese community in Jacksonville.
        </p>
        <Link 
          to="/contact"
          className="btn-primary bg-white text-[#172033] hover:bg-[#f4d5da]"
        >
          {t('sponsors.clickInfo')}
        </Link>
      </div>

      {selectedSponsorImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={selectedSponsorImage.alt}
          onClick={() => setSelectedSponsorImage(null)}
        >
          <div
            className="relative w-full max-w-3xl rounded-lg bg-white p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedSponsorImage(null)}
              className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-gray-700 shadow-md transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
              aria-label="Close image preview"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={selectedSponsorImage.src}
              alt={selectedSponsorImage.alt}
              className="mx-auto max-h-[80vh] w-full rounded-md object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorsSection; 
